// MissionTaskChecklistAddLinkModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { suggestChecklistAddLinkDefaultTitle } from 'utilities/ChecklistItemActions';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalMessage from 'components/modal/message/ModalMessage';
import TextInput from 'components/textinput/TextInput';

const DEFAULT_ADD_LINK_LABEL = 'Add Link';

/**
 * MissionTaskChecklistAddLinkModal
 *
 * Prompt for the runtime button label and optional default LINK node title
 * when adding an Add Link checklist action (e.g. "Add Contract URL" /
 * "Contract URL").
 *
 * @param {boolean} isOpen
 * @param {function} setOpen
 * @param {function} [onConfirm] - ({ label, defaultTitle }) => void
 * @returns {ReactNode}
 */
const MissionTaskChecklistAddLinkModal = React.memo(({
  isOpen,
  setOpen,
  onConfirm,
}) => {
  const isMobile = useIsMobile();
  const [label, setLabel] = useState(DEFAULT_ADD_LINK_LABEL);
  const [defaultTitle, setDefaultTitle] = useState(
    () => suggestChecklistAddLinkDefaultTitle(DEFAULT_ADD_LINK_LABEL),
  );
  const [defaultTitleTouched, setDefaultTitleTouched] = useState(false);
  const [labelError, setLabelError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setLabel(DEFAULT_ADD_LINK_LABEL);
      setDefaultTitle(suggestChecklistAddLinkDefaultTitle(DEFAULT_ADD_LINK_LABEL));
      setDefaultTitleTouched(false);
      setLabelError('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setOpen?.(false);
  };

  const handleAdd = () => {
    const nextLabel = String(label || '').trim();
    if (!nextLabel) {
      setLabelError('Enter a button label.');
      return;
    }
    const nextDefaultTitle = String(defaultTitle || '').trim()
      || suggestChecklistAddLinkDefaultTitle(nextLabel);
    onConfirm?.({
      label: nextLabel,
      defaultTitle: nextDefaultTitle,
    });
    setOpen?.(false);
  };

  if (!isOpen) return null;

  return (
    <Modal
      title="Add Link"
      isOpen={isOpen}
      height="auto"
      width={isMobile ? '100%' : '420px'}
      onClose={handleClose}
    >
      <ModalForm>
        <ModalMessage
          text="Checklist users will tap this button to attach a link to the task. Choose a clear label, and optionally the default title for the link they create."
        />
        <ModalField label="Button label">
          <TextInput
            autoFocus
            autoSelect
            error={!!labelError}
            helperText={labelError}
            maxLength={80}
            placeholder={DEFAULT_ADD_LINK_LABEL}
            value={label}
            onChange={(event) => {
              const nextLabel = event.target.value;
              setLabel(nextLabel);
              if (labelError) setLabelError('');
              if (!defaultTitleTouched) {
                setDefaultTitle(suggestChecklistAddLinkDefaultTitle(nextLabel));
              }
            }}
            onEnterPress={handleAdd}
            onEscapePress={handleClose}
          />
        </ModalField>
        <ModalField label="Default link title">
          <TextInput
            helperText="Used as the LINK node title when the button is tapped."
            maxLength={160}
            placeholder={suggestChecklistAddLinkDefaultTitle(label) || 'Link'}
            value={defaultTitle}
            onChange={(event) => {
              setDefaultTitleTouched(true);
              setDefaultTitle(event.target.value);
            }}
            onEnterPress={handleAdd}
            onEscapePress={handleClose}
          />
        </ModalField>
      </ModalForm>

      <ModalButtons>
        <Button
          iconKey="close"
          label="Cancel"
          onClick={handleClose}
        />
        <Button
          iconKey="check"
          label="Add"
          onClick={handleAdd}
        />
      </ModalButtons>
    </Modal>
  );
});

MissionTaskChecklistAddLinkModal.displayName = 'MissionTaskChecklistAddLinkModal';

export default MissionTaskChecklistAddLinkModal;
